import { useEffect, useRef, useState } from 'react';

interface UseScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

export const useScrollAnimation = ({
  // threshold 0 (qualquer pixel visivel) e nao 0.1 (10% da secao visivel).
  //
  // Com 0.1, uma secao mais alta que 10x a viewport NUNCA satisfaz a condicao:
  // 10% de 10.000px sao 1.000px, e a tela do celular tem ~700px. Isso passou
  // despercebido enquanto o site era SPA porque a secao comecava curta (com
  // skeleton), o observer disparava ali e o `triggerOnce` a mantinha visivel
  // quando o conteudo chegava. Com o HTML ja completo desde o servidor, a lista
  // do /blog nascia com 33 cards e ficava presa em opacity-0 -- pagina em
  // branco abaixo do titulo.
  threshold = 0,
  rootMargin = '0px',
  triggerOnce = true,
}: UseScrollAnimationOptions = {}) => {
  const ref = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
};

interface AnimatedSectionProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  id?: string;
}

export const AnimatedSection = ({ children, className = '', delay = 0, id }: AnimatedSectionProps) => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section
      ref={ref}
      id={id}
      // `secao-animada` existe para o fallback de <noscript> no index.html:
      // o HTML estatico ja traz o conteudo, mas ele sai com opacity-0 e quem
      // estiver sem JavaScript nao veria nada.
      className={`secao-animada transition-all duration-700 ease-out ${
        isVisible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-8'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </section>
  );
};
